import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Image,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity, ReferenceField } from '@uncinc/react-drupal-core/types';
import { BreadcrumbsV2 } from '@uncinc/react-drupal-core';

import type { ProfileDefault } from '../../../../types/uncinc_profile/profile';
import type { TaxonomyDepartment, TaxonomyLocation, TaxonomyTheme } from '../../../../types/uncinc_profile/taxonomy';
import {
  ButtonWrapper,
  ParagraphsRenderer,
  RelatedContent,
  Tags,
} from '../../../../components';
import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import { ReactComponent as Avatar } from '../../../../assets/images/avatar.svg';
import { RELATED_ARTICLES_HITS_PER_PAGE, RELATED_PROJECTS_HITS_PER_PAGE } from '../../../../config/constants';
import ORCIDPublications from './ORCIDPublications';

import './style.scss';

interface ProfileDetailsProps {
  telephone?: string;
  email?: string;
  displayContactDetails?: boolean;
  profileLocations?: ReferenceField<TaxonomyLocation>[];
  profileDepartments?: ReferenceField<TaxonomyDepartment>[];
  profileThemes?: ReferenceField<TaxonomyTheme>[];
  university?: string;
  promotor?: string;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({
  telephone,
  email,
  displayContactDetails,
  profileLocations,
  profileDepartments,
  profileThemes,
  university,
  promotor,
}) => {
  const { t } = useTranslation();

  if (
    !telephone &&
    !email &&
    profileLocations?.length === 0 &&
    profileDepartments?.length === 0
  ) {
    return null;
  }

  return (
    <div className="ProfilePage__details">
      {displayContactDetails && (
        <h2 className="label--regular">{t('content_types.profile.contact_details')}</h2>
      )}
      {(telephone && displayContactDetails) && (
        <ButtonWrapper
          className="ProfilePage__details__link ProfilePage__details__link__phone"
          href={`tel:${telephone}`}
          type="textual"
          iconName="call"
          iconPosition="left"
        >
          {telephone}
        </ButtonWrapper>
      )}
      {(email && displayContactDetails) && (
        <ButtonWrapper
          className="ProfilePage__details__link ProfilePage__details__link__email"
          href={`mailto:${email}`}
          type="textual"
          iconName="mail"
          iconPosition="left"
        >
          {email}
        </ButtonWrapper>
      )}
      {((profileLocations && profileLocations.length > 0) && displayContactDetails) && (
        <>
          {profileLocations.map((location) => (
            <ButtonWrapper
              className="ProfilePage__details__link ProfilePage__details__link__location"
              href={location.url}
              type="textual"
              iconName="location_on"
              iconPosition="left"
            >
              {location.entity.name[0].value}
            </ButtonWrapper>
          ))}
        </>
      )}
      {(profileDepartments && profileDepartments.length > 0) && (
        <div className="ProfilePage__details__departments">
          <Tags
            title={t('taxonomies.departments.title', { count: profileDepartments.length })}
            items={taxonomyTagEntityMapper(profileDepartments)}
          />
        </div>
      )}

      {(profileThemes && profileThemes.length > 0) && (
        <div className="ProfilePage__hero__themes">
          <Tags
            title={t('taxonomies.themes.title', { count: profileThemes.length })}
            items={taxonomyTagEntityMapper(profileThemes)}
          />
        </div>
      )}

      {(university || promotor) && (
        <div className="ProfilePage__details__student-details">
          {university && (
            <div>
              <p className="label--regular">{t('content_types.profile.university')}</p>
              {university}
            </div>
          )}
          {promotor && (
            <div>
              <p className="label--regular">{t('content_types.profile.promotor')}</p>
              {promotor}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ProfileProps {
  entity: ImmutableEntity<ProfileDefault>;
  className?: string;
}

const ProfilePage: React.FC<ProfileProps> = ({
  entity,
  className,
}) => {
  const classes = classNames(className, 'Node', 'ProfilePage');
  const { t } = useTranslation();

  const profile = entity.toJS();

  // External profiles don't have a URL alias, but can still be reached through /taxonomy/{id}
  // On the off-chance that someone finds that URL, don't render anything.
  if (profile.type[0].target_id === 'external_profile') {
    return null;
  }

  const profileId = profile.profile_id[0].value;
  const langcode = profile.langcode[0].value;
  const profileFirstName = profile.field_first_name[0].value;
  const profileLastName = profile.field_last_name[0].value;
  const educationalTitle = profile.field_educational_title[0]?.value;
  const fullName = [educationalTitle, profileFirstName, profileLastName].join(' ');

  const profileDescription = profile.field_description?.[0]?.processed;
  const profileResearchInterests = profile.field_research_interests?.[0]?.value;
  const profileSecondaryPositions = profile.field_secondary_positions[0]?.value;

  const email = profile.field_email_address?.[0]?.value;
  const telephone = profile.field_telephone?.[0]?.value;
  const displayContactDetails = profile.field_display_contact_details[0]?.value;
  const profileFunctions =
    taxonomyTagEntityMapper(profile.field_functions).map(
      (profileFunction) => profileFunction.name,
    ).join(', ');
  const profileLocations = profile.field_locations;
  const profileDepartments = profile.field_departments;
  const profileThemes = profile.field_themes;
  const profilePicture = profile.field_media_item[0];
  const university = profile.field_university?.[0]?.value;
  const promotor = profile.field_promotor?.[0]?.value;

  const orcidPublications = profile.field_orcid_publication_ids[0]?.value.split(',') ?? [];

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="ProfilePage__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} currentPageTitle={fullName} />
            </div>
          </div>
          <div className="row">
            <div className="ProfilePage__main-section col-12 col-md-10 col-lg-8 col-xl-7">
              <section className="ProfilePage__hero">
                <div className="ProfilePage__hero__header">
                  <div className="ProfilePage__hero__header__heading">
                    <h1 itemProp="name" className="ProfilePage__hero__name">{fullName}</h1>
                    {(profileFunctions && profileFunctions.length > 0) && (
                      <div className="ProfilePage__hero__functions body--large">
                        {profileFunctions}
                      </div>
                    )}
                  </div>

                  {profilePicture?.entity ? (
                    <div className="ProfilePage__hero__image">
                      <Image
                        src={[profilePicture.styles.ratio_1_1_xs]}
                        lazyload={false}
                        alt=""
                        role="presentation"
                      />
                    </div>
                  ) : (
                    <div className="ProfilePage__hero__image">
                      <Avatar role="presentation" />
                    </div>
                  )}
                </div>

                {profileDescription && (
                  <WysiwygField
                    className="ProfilePage__hero__description body--large"
                    property="schema:text"
                    html={profileDescription}
                  />
                )}
                <div className="ProfilePage__main-section__profile-details">
                  <ProfileDetails
                    telephone={telephone}
                    email={email}
                    profileLocations={profileLocations}
                    profileDepartments={profileDepartments}
                    profileThemes={profileThemes}
                    university={university}
                    promotor={promotor}
                    displayContactDetails={displayContactDetails}
                  />
                </div>
                {profileResearchInterests && (
                  <div className="ProfilePage__hero__research-interests">
                    <h2>{t('content_types.profile.research_interests')}</h2>
                    <ul>
                      {profileResearchInterests.split(/,|;/).map((interest) => (<li>{interest.trim()}</li>))}
                    </ul>
                  </div>
                )}
                {profileSecondaryPositions && (
                  <div className="ProfilePage__hero__additional-functions">
                    <h2 className="asH3">{t('content_types.profile.additional_functions')}</h2>
                    <ul>
                      {profileSecondaryPositions.split(/\/\//).map((secondaryPosition) => (<li>{secondaryPosition.trim()}</li>))}
                    </ul>
                  </div>
                )}

                <ParagraphsRenderer className="ProfilePage__paragraphs" paragraphs={entity.get('field_paragraphs')} noBootstrap />
              </section>
            </div>

            <div className="ProfilePage__sidebar col-12 col-md-10 col-lg-4 col-xl-3 offset-xl-1">
              <section className="ProfilePage__sidebar__profile-details">
                <ProfileDetails
                  telephone={telephone}
                  email={email}
                  profileLocations={profileLocations}
                  profileDepartments={profileDepartments}
                  profileThemes={profileThemes}
                  university={university}
                  promotor={promotor}
                  displayContactDetails={displayContactDetails}
                />
              </section>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-10">
              {orcidPublications.length > 0 && (
                <div className="ProfilePage__publications">
                  <ORCIDPublications publications={orcidPublications} />
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>

      <div className="FixedReferenceBlocks">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10">
              <div id="articles" className="ProfilePage__articles">
                <RelatedContent
                  title={t('content_types.article.type')}
                  contentType="article"
                  hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
                  filterNames={['linked_staff']}
                  filterValues={[profileId]}
                  langcode={langcode}
                />
              </div>
              <div className="ProfilePage__projects">
                <RelatedContent
                  title={t('content_types.project.type_plural')}
                  contentType="project"
                  hitsPerPage={RELATED_PROJECTS_HITS_PER_PAGE}
                  filterNames={['linked_staff', 'project_research_leader']}
                  filterValues={[profileId]}
                  langcode={langcode}
                />
              </div>
            </div>
          </div>
        </Container>
      </div>
    </article>
  );
};

export default ProfilePage;
